'use client'

import React, { useCallback, useState } from 'react'

import { Button, Check, Input, mergeClassName, Pencil, Tooltip, X } from '../index'

interface EditableFieldProps {
  value: string
  onSubmit: (value: string) => void
  canEdit?: boolean
  isLoading?: boolean
  placeholder?: string
  tooltipContent?: string
  variant?: 'default' | 'large'
  displayComponent?: React.ReactNode
  inputClassName?: string
  displayClassName?: string
  containerClassName?: string
}

const EditableControls = ({
  onSubmit,
  onCancel,
  isLoading
}: {
  onSubmit: () => void
  onCancel: () => void
  isLoading?: boolean
}) => {
  return (
    <div className="flex gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={onSubmit}
        className="h-8 w-8 p-0"
        loading={isLoading}
        disabled={isLoading}
      >
        <Check className="h-4 w-4" />
        <span className="sr-only">Confirm change</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="h-8 w-8 p-0"
        loading={isLoading}
        disabled={isLoading}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Cancel change</span>
      </Button>
    </div>
  )
}

export const EditableField = ({
  value,
  onSubmit,
  canEdit = true,
  isLoading = false,
  placeholder = 'Click to edit',
  tooltipContent = 'Click to edit',
  variant = 'default',
  displayComponent,
  inputClassName,
  displayClassName,
  containerClassName
}: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(value)

  const handleSubmit = useCallback(() => {
    if (value === inputValue.trim() || !inputValue.trim()) {
      setIsEditing(false)
      setInputValue(value)
      return
    }
    onSubmit(inputValue.trim())
    setIsEditing(false)
  }, [inputValue, value, onSubmit])

  const handleCancel = () => {
    setIsEditing(false)
    setInputValue(value)
  }

  // Styles based on variant
  const variantStyles = {
    default: {
      container: 'flex items-center w-full gap-2',
      input: 'rounded-md p-2 border-2 h-8',
      display: ' font-semibold break-words line-clamp-2 group-hover:bg-accent dark:group-hover:bg-accent/50 py-1 px-2 rounded transition-colors duration-200 w-full text-left'
    },
    large: {
      container: 'flex items-center w-full gap-4 px-2',
      input: 'py-2 px-4 text-lg font-bold',
      display:
        'text-lg font-bold break-words line-clamp-2 group-hover:bg-accent dark:group-hover:bg-accent/50 py-1 px-2 rounded transition-colors duration-200 w-full text-left'
    }
  }

  if (!canEdit) {
    return (
      <div className={containerClassName}>
        {displayComponent || (
          <span className={mergeClassName(variantStyles[variant].display, displayClassName)}>{value}</span>
        )}
      </div>
    )
  }

  return (
    <div className={mergeClassName(variantStyles[variant].container, containerClassName)}>
      {isEditing ? (
        <>
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className={mergeClassName(variantStyles[variant].input, inputClassName)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit()
              if (e.key === 'Escape') handleCancel()
            }}
            placeholder={placeholder}
            autoFocus={true}
          />
          <EditableControls onSubmit={handleSubmit} onCancel={handleCancel} isLoading={isLoading} />
        </>
      ) : (
        <Tooltip content={tooltipContent} side="bottom" delay={500}>
          <button onClick={() => setIsEditing(true)} className="flex w-full items-center group">
            {displayComponent || (
              <span className={mergeClassName(variantStyles[variant].display, displayClassName)}>{value}</span>
            )}
            <Pencil className="h-4 w-4 opacity-0 group-hover:opacity-100 ml-2 shrink-0" />
          </button>
        </Tooltip>
      )}
    </div>
  )
}
